import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ArrowLeft, Loader2, Trophy, Check, Target, Scale, Ruler, CreditCard, Lock, Shield, Tag, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { fetchApi } from '../lib/utils';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const GOALS = [
  { id: 'fat_loss', label: 'Lose Fat', desc: 'Burn fat while keeping muscle' },
  { id: 'muscle_gain', label: 'Build Muscle', desc: 'Gain lean muscle mass' },
  { id: 'general_fitness', label: 'Get Fit', desc: 'Improve overall fitness' },
  { id: 'event_prep', label: 'Event Prep', desc: 'Train for a specific event' },
];

const PLANS = [
  { id: 'monthly', name: 'Monthly', price: 10, period: '/month', priceId: 'price_monthly' },
  { id: 'quarterly', name: '3 Months', price: 27, period: '/3 months', priceId: 'price_quarterly', popular: true, savings: 'Save 10%' },
  { id: 'yearly', name: 'Yearly', price: 96, period: '/year', priceId: 'price_yearly', savings: 'Save 20%' },
];

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      '::placeholder': { color: '#71717a' },
      iconColor: '#CCFF00',
    },
    invalid: { color: '#ef4444', iconColor: '#ef4444' },
  },
};

function RegisterForm() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isChallenge = searchParams.get('challenge') === 'true';
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const { t } = useLanguage();
  const stripe = useStripe();
  const elements = useElements();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    height: '',
    weight: '',
    goal: '',
    plan: 'quarterly',
    promoCode: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cardComplete, setCardComplete] = useState(false);
  const [promoStatus, setPromoStatus] = useState({ valid: null, message: '', data: null });
  const [validatingPromo, setValidatingPromo] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    if (e.target.name === 'promoCode') {
      setPromoStatus({ valid: null, message: '', data: null });
    }
  };

  const validatePromoCode = async () => {
    if (!formData.promoCode.trim()) return;
    
    setValidatingPromo(true);
    try {
      const response = await fetchApi('/subscription/validate-promo', {
        method: 'POST',
        body: JSON.stringify({ code: formData.promoCode.trim() }),
      });
      
      if (response.valid) {
        setPromoStatus({
          valid: true,
          message: response.description,
          data: response
        });
      } else {
        setPromoStatus({
          valid: false,
          message: response.message || 'Invalid promo code',
          data: null
        });
      }
    } catch (err) {
      setPromoStatus({
        valid: false,
        message: 'Invalid promo code',
        data: null
      });
    } finally {
      setValidatingPromo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (!formData.goal) {
      setError('Please select a goal');
      return;
    }
    if (!formData.height || !formData.weight) {
      setError('Please enter your height and weight');
      return;
    }
    if (!stripe || !elements) {
      setError('Payment system not loaded. Please refresh and try again.');
      return;
    }
    if (!cardComplete) {
      setError('Please enter your card details');
      return;
    }

    setLoading(true);

    try {
      // 1. Register user first
      const registerResponse = await register(formData.name, formData.email, formData.password, {
        height: parseFloat(formData.height),
        weight: parseFloat(formData.weight),
        goal: formData.goal,
        selectedPlan: formData.plan,
      });

      // 2. Create payment method with Stripe
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: formData.name,
          email: formData.email,
        },
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // 3. Create subscription with trial
      const selectedPlanData = PLANS.find(p => p.id === formData.plan);
      await fetchApi('/subscription/create-trial', {
        method: 'POST',
        body: JSON.stringify({
          payment_method_id: paymentMethod.id,
          plan_id: formData.plan,
          price: selectedPlanData.price,
          promo_code: promoStatus.valid ? formData.promoCode.trim().toUpperCase() : null,
        }),
      });

      // 4. Navigate to onboarding for personalization questions
      navigate(isChallenge ? '/onboarding?challenge=true' : '/onboarding');
      
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = PLANS.find(p => p.id === formData.plan);

  return (
    <div className="min-h-screen bg-black py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>

          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#CCFF00] flex items-center justify-center">
              <span className="text-black font-black text-xl">V</span>
            </div>
            <span className="font-bold text-xl">VYTA</span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-black mb-2">
            Start Your 7-Day Free Trial
          </h1>
          <p className="text-white/70 mb-6">
            Get your personalised fitness & nutrition plan. Cancel anytime.
          </p>

          {/* Challenge Banner */}
          {isChallenge && (
            <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-yellow-400/10 via-amber-500/10 to-yellow-400/10 border border-yellow-400/30">
              <div className="flex items-center gap-3">
                <Trophy className="w-8 h-8 text-yellow-400 flex-shrink-0" />
                <div>
                  <p className="font-bold text-yellow-400">5 Week Challenge</p>
                  <p className="text-sm text-zinc-300">Win a share of USD $2,000</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Section 1: Account Details */}
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-[#CCFF00] text-black text-sm flex items-center justify-center font-bold">1</span>
                Account Details
              </h2>
              
              <div>
                <Label className="text-white/80">Full Name</Label>
                <Input
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  required
                  className="mt-2 bg-zinc-900 border-zinc-800 focus:border-[#CCFF00] h-12 text-white"
                  data-testid="name-input"
                />
              </div>

              <div>
                <Label className="text-white/80">Email</Label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                  className="mt-2 bg-zinc-900 border-zinc-800 focus:border-[#CCFF00] h-12 text-white"
                  data-testid="email-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80">Password</Label>
                  <div className="relative mt-2">
                    <Input
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      required
                      className="bg-zinc-900 border-zinc-800 focus:border-[#CCFF00] h-12 pr-10 text-white"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-white/80">Confirm Password</Label>
                  <Input
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    className="mt-2 bg-zinc-900 border-zinc-800 focus:border-[#CCFF00] h-12 text-white"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Body Measurements */}
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-[#CCFF00] text-black text-sm flex items-center justify-center font-bold">2</span>
                Your Measurements
              </h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80 flex items-center gap-2">
                    <Ruler className="w-4 h-4" /> Height (cm)
                  </Label>
                  <Input
                    name="height"
                    type="number"
                    value={formData.height}
                    onChange={handleChange}
                    placeholder="175"
                    required
                    className="mt-2 bg-zinc-900 border-zinc-800 focus:border-[#CCFF00] h-12 text-white"
                    data-testid="height-input"
                  />
                </div>
                <div>
                  <Label className="text-white/80 flex items-center gap-2">
                    <Scale className="w-4 h-4" /> Weight (kg)
                  </Label>
                  <Input
                    name="weight"
                    type="number"
                    value={formData.weight}
                    onChange={handleChange}
                    placeholder="75"
                    required
                    className="mt-2 bg-zinc-900 border-zinc-800 focus:border-[#CCFF00] h-12 text-white"
                    data-testid="weight-input"
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Goals */}
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-[#CCFF00] text-black text-sm flex items-center justify-center font-bold">3</span>
                Your Goal
              </h2>
              
              <div className="grid grid-cols-2 gap-3">
                {GOALS.map((goal) => (
                  <button
                    key={goal.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, goal: goal.id })}
                    className={`p-4 rounded-xl text-left transition-all ${
                      formData.goal === goal.id
                        ? 'bg-[#CCFF00]/20 border-2 border-[#CCFF00]'
                        : 'bg-zinc-900 border-2 border-zinc-800 hover:border-zinc-700'
                    }`}
                    data-testid={`goal-${goal.id}`}
                  >
                    <Target className={`w-5 h-5 mb-2 ${formData.goal === goal.id ? 'text-[#CCFF00]' : 'text-zinc-500'}`} />
                    <h4 className="font-bold text-sm text-white">{goal.label}</h4>
                    <p className="text-xs text-white/60 mt-1">{goal.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Section 4: Plan Selection */}
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-[#CCFF00] text-black text-sm flex items-center justify-center font-bold">4</span>
                Select Plan <span className="text-sm font-normal text-white/60">(after trial ends)</span>
              </h2>
              
              <div className="grid grid-cols-3 gap-3">
                {PLANS.map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, plan: plan.id })}
                    className={`p-4 rounded-xl text-center transition-all relative ${
                      formData.plan === plan.id
                        ? 'bg-[#CCFF00]/20 border-2 border-[#CCFF00]'
                        : 'bg-zinc-900 border-2 border-zinc-800 hover:border-zinc-700'
                    }`}
                    data-testid={`plan-${plan.id}`}
                  >
                    {plan.popular && (
                      <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#CCFF00] text-black text-xs font-bold px-2 py-0.5 rounded-full">
                        Popular
                      </span>
                    )}
                    <h4 className="font-bold text-sm mb-1 text-white">{plan.name}</h4>
                    <p className="text-xl font-black text-white">${plan.price}</p>
                    <p className="text-xs text-white/60">{plan.period}</p>
                    {plan.savings && (
                      <p className="text-xs text-[#CCFF00] mt-1 font-medium">{plan.savings}</p>
                    )}
                    {formData.plan === plan.id && (
                      <Check className="w-5 h-5 text-[#CCFF00] absolute top-2 right-2" />
                    )}
                  </button>
                ))}
              </div>
              
              {/* Promo Code */}
              <div className="mt-4">
                <Label className="text-white/80 flex items-center gap-2">
                  <Tag className="w-4 h-4" /> Have a promo code?
                </Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    name="promoCode"
                    type="text"
                    value={formData.promoCode}
                    onChange={handleChange}
                    placeholder="Enter code"
                    className="bg-zinc-900 border-zinc-800 focus:border-[#CCFF00] h-12 text-white uppercase"
                    data-testid="promo-input"
                  />
                  <Button
                    type="button"
                    onClick={validatePromoCode}
                    disabled={validatingPromo || !formData.promoCode.trim()}
                    variant="outline"
                    className="border-zinc-700 text-white hover:bg-zinc-800 px-6 h-12"
                  >
                    {validatingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                  </Button>
                </div>
                {promoStatus.valid === true && (
                  <div className="flex items-center gap-2 mt-2 text-emerald-400 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span>{promoStatus.message}</span>
                    {promoStatus.data?.trial_days > 7 && (
                      <span className="bg-emerald-500/20 px-2 py-0.5 rounded text-xs">{promoStatus.data.trial_days}-day trial!</span>
                    )}
                    {promoStatus.data?.discount_percent > 0 && (
                      <span className="bg-emerald-500/20 px-2 py-0.5 rounded text-xs">{promoStatus.data.discount_percent}% off!</span>
                    )}
                  </div>
                )}
                {promoStatus.valid === false && (
                  <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                    <XCircle className="w-4 h-4" />
                    <span>{promoStatus.message}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Section 5: Payment */}
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-[#CCFF00] text-black text-sm flex items-center justify-center font-bold">5</span>
                Payment Details
              </h2>
              
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="w-5 h-5 text-[#CCFF00]" />
                  <span className="text-white/80 text-sm">Secure card payment</span>
                  <Lock className="w-4 h-4 text-zinc-500 ml-auto" />
                </div>
                <CardElement 
                  options={cardElementOptions}
                  onChange={(e) => setCardComplete(e.complete)}
                />
              </div>
              
              {/* Trial Notice */}
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-emerald-400 font-bold text-sm">Your card will NOT be charged today</p>
                    <p className="text-white/70 text-sm mt-1">
                      You'll have full access for {promoStatus.data?.trial_days || 7} days free. Cancel anytime before the trial ends and you won't be charged. 
                      {promoStatus.data?.discount_percent > 0 ? (
                        <> After the trial, you'll be billed <span className="line-through text-white/50">${selectedPlan?.price}</span> <span className="text-emerald-400 font-bold">${(selectedPlan?.price * (1 - promoStatus.data.discount_percent / 100)).toFixed(2)}</span>{selectedPlan?.period}.</>
                      ) : (
                        <> After the trial, you'll be billed ${selectedPlan?.price}{selectedPlan?.period}.</>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="pt-4">
              <Button
                type="submit"
                disabled={loading || !stripe}
                className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold h-14 text-lg rounded-full"
                data-testid="start-trial-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>Start {promoStatus.data?.trial_days || 7}-Day Free Trial</>
                )}
              </Button>
              
              <p className="text-center text-white/50 text-sm mt-4">
                By signing up, you agree to our Terms of Service and Privacy Policy.
              </p>
            </div>
          </form>

          <p className="text-center text-white/60 mt-6">
            Already have an account?{' '}
            <button onClick={() => navigate('/login')} className="text-[#CCFF00] hover:underline font-medium">
              Log In
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}

// Login component (simpler)
function LoginForm() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>

        <div className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#CCFF00] flex items-center justify-center">
            <span className="text-black font-black text-xl">V</span>
          </div>
          <span className="font-bold text-xl">VYTA</span>
        </div>

        <h1 className="text-3xl font-black mb-2">Welcome Back</h1>
        <p className="text-white/70 mb-8">Log in to continue your fitness journey</p>

        {error && (
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label className="text-white/80">Email</Label>
            <Input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              required
              className="mt-2 bg-zinc-900 border-zinc-800 focus:border-[#CCFF00] h-12 text-white"
              data-testid="email-input"
            />
          </div>

          <div>
            <Label className="text-white/80">Password</Label>
            <div className="relative mt-2">
              <Input
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
                className="bg-zinc-900 border-zinc-800 focus:border-[#CCFF00] h-12 pr-12 text-white"
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold h-12"
            data-testid="login-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Log In'}
          </Button>
        </form>

        <p className="text-center text-white/60 mt-6">
          Don't have an account?{' '}
          <button onClick={() => navigate('/register')} className="text-[#CCFF00] hover:underline font-medium">
            Start Free Trial
          </button>
        </p>
      </motion.div>
    </div>
  );
}

export default function Auth() {
  const location = useLocation();
  const isLogin = location.pathname === '/login';

  if (isLogin) {
    return <LoginForm />;
  }

  return (
    <Elements stripe={stripePromise}>
      <RegisterForm />
    </Elements>
  );
}
